import { memo, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import type { GetAssistantSessionsBySessionIdMessages200MessagesItem as ChatMessage } from '@/api/generated/lucioAPI.schemas';
import { Spinner } from '@/components/ui/spinner';
import { useChatScroll } from '@/features/assistant/hooks/useChatScroll';

import type { ActivityStep } from '../utils/activitySteps';
import { ActivityTrail } from './ActivityTrail';
import MessageBubble from './Message';
import { ThinkingIndicator } from './ThinkingIndicator';

interface ChatMessagesProps {
  /** Message the activity trail belongs to; omit to render no trail. */
  activityMessageId?: string | undefined;
  activitySteps?: Array<ActivityStep>;
  fetchNextPage?: () => void;
  footer?: React.ReactNode;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  isStreaming?: boolean;
  messages: Array<ChatMessage>;
  onViewMail?: ((messageId: string) => void) | undefined;
}

export const ChatMessages = memo(
  ({
    activityMessageId,
    activitySteps = [],
    fetchNextPage,
    footer,
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isStreaming = false,
    messages,
    onViewMail,
  }: ChatMessagesProps) => {
    const { messagesEndRef, scrollContainerId, scrollContainerRef, spacerRef } = useChatScroll({
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      isStreaming,
      messages,
    });

    const handleFetchNextPage = useCallback(() => {
      if (hasNextPage && fetchNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

    if (messages.length === 0) {
      return null;
    }

    return (
      <div
        className="custom-scrollbar flex-1 overflow-y-auto"
        ref={scrollContainerRef}
        style={{ height: '100%', position: 'relative' }}
      >
        {scrollContainerId && (
          <InfiniteScroll
            className="xs:px-6 space-y-2 px-4 py-4"
            dataLength={messages.length}
            hasMore={hasNextPage}
            inverse
            loader={
              <div className="flex justify-center py-4">
                <Spinner className="text-primary size-6" />
              </div>
            }
            next={handleFetchNextPage}
            scrollableTarget={scrollContainerId}
            scrollThreshold={0.95}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              const isAssistantMessage = message.role === 'ASSISTANT';
              const isStreamingMessage = isStreaming && isLastMessage;

              // New Indicator Logic
              const showThinkingIndicator = isLoading && isLastMessage && isAssistantMessage;

              return (
                <div data-role={message.role} key={message.id}>
                  {showThinkingIndicator && (
                    <div className="-mt-1 -mb-1">
                      <ThinkingIndicator />
                    </div>
                  )}

                  {activityMessageId === message.id && (
                    <div className="xs:-mx-6 -mx-4">
                      <ActivityTrail steps={activitySteps} />
                    </div>
                  )}

                  <MessageBubble
                    isGenerating={isStreamingMessage}
                    message={message}
                    onViewMail={onViewMail}
                  />
                </div>
              );
            })}
          </InfiniteScroll>
        )}

        {footer}
        <div aria-hidden="true" ref={spacerRef} style={{ height: 0 }} />
        <div aria-hidden="true" className="h-px w-full shrink-0" ref={messagesEndRef} />
      </div>
    );
  },
);

ChatMessages.displayName = 'ChatMessages';
